import { useEffect, useRef, useState } from "react";
import { Client } from "../../../models/ClientModels";
import { Branch } from "../../../models/BranchModels";
import { Service } from "../../../models/ServiceModels";
import { User } from "../../../models/User";
import {
  Appointment,
  AppointmentStatus,
} from "../../../models/AppointmentModels";
import { toDateTimeLocal } from "../../../utils/DateTime";
import "./AppointmentModal.css";

export type AppointmentModalMode = "create" | "edit";

interface AppointmentModalProps {
  isOpen: boolean;
  mode: AppointmentModalMode;
  appointment: Appointment | null;
  branches: Branch[];
  services: Service[];
  staff: User[];
  submitting: boolean;
  lookupClient: (phone: string) => Promise<Client | null>;
  appointmentClient?: {
    fullName?: string | null;
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    age?: number | null;
  } | null;
  preselectedClient?: Client | null;
  onNewClient: (phone: string) => void;
  canCreateClient: boolean;
  onClose: () => void;
  onSubmit: (form: AppointmentFormValues) => Promise<void>;
}

export interface AppointmentServiceLine {
  serviceId: string;
  quantity: string;
}

export interface AppointmentFormValues {
  clientId: string;
  branchId: string;
  staffUserId: string;
  staffName: string;
  status: string;
  scheduledAt: string;
  notes: string;
  services: AppointmentServiceLine[];
}

const emptyForm: AppointmentFormValues = {
  clientId: "",
  branchId: "",
  staffUserId: "",
  staffName: "",
  status: String(AppointmentStatus.Scheduled),
  scheduledAt: "",
  notes: "",
  services: [{ serviceId: "", quantity: "1" }],
};

const buildDefaultForm = (branches: Branch[]): AppointmentFormValues => ({
  ...emptyForm,
  branchId: branches[0]?.id ?? "",
  scheduledAt: toDateTimeLocal(new Date().toISOString()),
});

const fromAppointment = (
  appointment: Appointment,
): AppointmentFormValues => ({
  clientId: appointment.clientId,
  branchId: appointment.branchId || "",
  staffUserId: appointment.staffUserId || "",
  staffName: appointment.staffName || "",
  status: String(appointment.status),
  scheduledAt: toDateTimeLocal(appointment.scheduledAt),
  notes: appointment.notes || "",
  services: appointment.services.length
    ? appointment.services.map((service) => ({
        serviceId: service.serviceId,
        quantity: String(service.quantity),
      }))
    : [{ serviceId: "", quantity: "1" }],
});

type LookupStatus = "idle" | "loading" | "found" | "not-found" | "error";

const AppointmentModal = ({
  isOpen,
  mode,
  appointment,
  branches,
  services,
  staff,
  submitting,
  lookupClient,
  appointmentClient,
  preselectedClient,
  onNewClient,
  canCreateClient,
  onClose,
  onSubmit,
}: AppointmentModalProps) => {
  const [form, setForm] = useState<AppointmentFormValues>(emptyForm);
  const [phoneInput, setPhoneInput] = useState("");
  const [lookupStatus, setLookupStatus] = useState<LookupStatus>("idle");
  const [foundClient, setFoundClient] = useState<Client | null>(null);

  const branchesRef = useRef(branches);
  branchesRef.current = branches;

  useEffect(() => {
    if (!isOpen) return;
    setPhoneInput("");
    setFoundClient(null);
    setLookupStatus("idle");
    if (mode === "create") {
      setForm(buildDefaultForm(branchesRef.current));
    } else if (appointment) {
      setForm(fromAppointment(appointment));
    }
  }, [isOpen, mode, appointment]);

  useEffect(() => {
    if (!isOpen || mode !== "create" || !preselectedClient) return;
    setFoundClient(preselectedClient);
    setPhoneInput(preselectedClient.phone ?? "");
    setLookupStatus("found");
    setForm((prev) => ({ ...prev, clientId: preselectedClient.id }));
  }, [isOpen, mode, preselectedClient]);

  if (!isOpen) return null;

  const setField = (key: keyof AppointmentFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetLookup = () => {
    setLookupStatus("idle");
    setFoundClient(null);
    setForm((prev) => ({ ...prev, clientId: "" }));
  };

  const handleLookup = async () => {
    const phone = phoneInput.trim();
    if (!phone) return;
    setLookupStatus("loading");
    try {
      const client = await lookupClient(phone);
      if (client) {
        setFoundClient(client);
        setLookupStatus("found");
        setForm((prev) => ({ ...prev, clientId: client.id }));
      } else {
        setFoundClient(null);
        setLookupStatus("not-found");
        setForm((prev) => ({ ...prev, clientId: "" }));
      }
    } catch {
      setFoundClient(null);
      setLookupStatus("error");
      setForm((prev) => ({ ...prev, clientId: "" }));
    }
  };

  const updateServiceLine = (
    index: number,
    patch: Partial<AppointmentServiceLine>,
  ) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.map((line, i) =>
        i === index ? { ...line, ...patch } : line,
      ),
    }));
  };

  const addServiceLine = () => {
    setForm((prev) => ({
      ...prev,
      services: [...prev.services, { serviceId: "", quantity: "1" }],
    }));
  };

  const removeServiceLine = (index: number) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.services.length === 0 || !form.services[0].serviceId) return;
    onSubmit(form);
  };

  const selectedServiceIds = form.services
    .map((line) => line.serviceId)
    .filter(Boolean);

  return (
    <div className="appt-modal-overlay">
      <div className="appt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="appt-modal__header">
          <h3>
            {mode === "create" ? "New Appointment" : "Edit Appointment"}
          </h3>
          <button
            className="appt-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="appt-modal__body" onSubmit={handleSubmit}>
          <div className="appt-form-grid">
            <div className="appt-form-group appt-form-group--full">
              <label htmlFor="appt-client-phone">
                {mode === "create" ? "Client Phone *" : "Client"}
              </label>

              {mode === "create" ? (
                <>
                  <div className="appt-client-row">
                    <input
                      id="appt-client-phone"
                      type="tel"
                      value={phoneInput}
                      placeholder="Enter client phone number"
                      onChange={(e) => {
                        setPhoneInput(e.target.value);
                        if (lookupStatus !== "idle") resetLookup();
                      }}
                    />
                    <button
                      type="button"
                      className="appt-btn appt-btn--secondary appt-btn--compact"
                      disabled={!phoneInput.trim() || lookupStatus === "loading"}
                      onClick={handleLookup}
                    >
                      {lookupStatus === "loading" ? "Searching…" : "Search"}
                    </button>
                  </div>

                  {lookupStatus === "loading" && (
                    <p className="appt-form-hint">Looking up client…</p>
                  )}

                  {lookupStatus === "not-found" && (
                    <div className="appt-lookup-empty">
                      <p className="appt-form-error">
                        No client found with this number.
                      </p>
                      {canCreateClient ? (
                        <button
                          type="button"
                          className="appt-btn appt-btn--primary appt-btn--compact"
                          onClick={() => onNewClient(phoneInput.trim())}
                        >
                          Add as New Client
                        </button>
                      ) : (
                        <p className="appt-form-error">
                          You don't have permission to add new clients.
                        </p>
                      )}
                    </div>
                  )}

                  {lookupStatus === "error" && (
                    <p className="appt-form-error">
                      Could not search. Please try again.
                    </p>
                  )}

                  {foundClient && lookupStatus === "found" && (
                    <div className="appt-client-card">
                      <div className="appt-client-card__name">
                        {foundClient.fullName}
                      </div>
                      <div className="appt-client-card__meta">
                        <span>Phone · {foundClient.phone}</span>
                        {foundClient.age != null && (
                          <span>Age · {foundClient.age}</span>
                        )}
                        {foundClient.gender && (
                          <span>Gender · {foundClient.gender}</span>
                        )}
                        {foundClient.address && (
                          <span>Address · {foundClient.address}</span>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="appt-client-card">
                  <div className="appt-client-card__name">
                    {appointmentClient?.fullName ?? "Unknown client"}
                  </div>
                  {appointmentClient?.phone && (
                    <div className="appt-client-card__meta">
                      <span>Phone · {appointmentClient.phone}</span>
                    </div>
                  )}
                  {appointmentClient?.age != null && (
                    <div className="appt-client-card__meta">
                      <span>Age · {appointmentClient.age}</span>
                    </div>
                  )}
                  {appointmentClient?.address && (
                    <div className="appt-client-card__meta">
                      <span>Address · {appointmentClient.address}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="appt-form-group">
              <label htmlFor="appt-branch">Branch</label>
              <select
                id="appt-branch"
                value={form.branchId}
                onChange={(e) => setField("branchId", e.target.value)}
              >
                <option value="">—</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="appt-form-group">
              <label htmlFor="appt-status">Status</label>
              <select
                id="appt-status"
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
              >
                <option value={AppointmentStatus.Scheduled}>Scheduled</option>
                <option value={AppointmentStatus.CheckedIn}>Checked In</option>
                <option value={AppointmentStatus.InProgress}>In Progress</option>
                <option value={AppointmentStatus.Completed}>Completed</option>
                <option value={AppointmentStatus.Cancelled}>Cancelled</option>
                <option value={AppointmentStatus.NoShow}>No Show</option>
                <option value={AppointmentStatus.WalkIn}>Walk In</option>
                {form.status === String(AppointmentStatus.ScheduledByPublicWeb) && (
                  <option value={AppointmentStatus.ScheduledByPublicWeb}>
                    Website booking
                  </option>
                )}
              </select>
            </div>

            <div className="appt-form-group appt-form-group--full">
              <label htmlFor="appt-scheduled">Date &amp; Time *</label>
              <div className="appt-datetime-row">
                <input
                  id="appt-scheduled"
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={(e) => setField("scheduledAt", e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="appt-btn appt-btn--secondary appt-btn--compact"
                  onClick={() =>
                    setField(
                      "scheduledAt",
                      form.scheduledAt ||
                        toDateTimeLocal(new Date().toISOString()),
                    )
                  }
                >
                  OK
                </button>
              </div>
            </div>

            <div className="appt-form-group appt-form-group--full">
              <label htmlFor="appt-staff">Assign Staff</label>
              <select
                id="appt-staff"
                value={form.staffUserId}
                onChange={(e) => {
                  setField("staffUserId", e.target.value);
                  const selectedUser = staff.find((u) => u.id === e.target.value);
                  setField("staffName", selectedUser?.userName || "");
                }}
              >
                <option value="">—</option>
                {staff.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.userName || user.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="appt-form-group appt-form-group--full">
            <label className="appt-form-section-label">Services *</label>
            {form.services.map((line, index) => (
              <div className="appt-service-line" key={index}>
                <select
                  aria-label={`Service ${index + 1}`}
                  value={line.serviceId}
                  onChange={(e) =>
                    updateServiceLine(index, { serviceId: e.target.value })
                  }
                >
                  <option value="">Select service…</option>
                  {services
                    .filter(
                      (service) =>
                        service.isActive &&
                        (service.id === line.serviceId ||
                          !selectedServiceIds.includes(service.id)),
                    )
                    .map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                        {service.price > 0
                          ? ` · ${service.currency} ${service.price}`
                          : ""}
                      </option>
                    ))}
                </select>
                <input
                  type="number"
                  min={1}
                  aria-label={`Quantity for service ${index + 1}`}
                  value={line.quantity}
                  onChange={(e) =>
                    updateServiceLine(index, { quantity: e.target.value })
                  }
                />
                <button
                  type="button"
                  className="appt-btn appt-btn--danger appt-btn--compact"
                  aria-label="Remove service"
                  disabled={form.services.length === 1}
                  onClick={() => removeServiceLine(index)}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="appt-btn appt-btn--secondary appt-btn--compact"
              onClick={addServiceLine}
            >
              + Add Service
            </button>
            {form.services.length === 0 && (
              <p className="appt-form-error">Add at least one service.</p>
            )}
          </div>

          <div className="appt-form-group appt-form-group--full">
            <label htmlFor="appt-notes">Notes</label>
            <textarea
              id="appt-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
            />
          </div>

          <div className="appt-modal__actions">
            <button
              type="button"
              className="appt-btn appt-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="appt-btn appt-btn--primary"
              disabled={
                submitting ||
                !form.clientId ||
                !form.scheduledAt ||
                form.services.length === 0 ||
                !form.services[0].serviceId
              }
            >
              {submitting
                ? "Saving..."
                : mode === "create"
                  ? "Create Appointment"
                  : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentModal;